"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.accelerate = exports.animate = void 0;

// @ts-check

/**
 * This function creates an s-shaped curve used to ease value between zero and one
 * https://en.wikipedia.org/wiki/Logistic_function
 * @param {number} x Number between zero and one
 */
var easeInOut = function easeInOut(x) {
  return 1 / (1 + Math.pow(Math.E, -10 * (x - 0.5)));
};
/**
 * @callback AnimateStepCallback
 * @param {number} value A new value between from and to depending on how much time has elapsed
 * @returns {void}
 */

/**
 * Transition from one value to another and fire a callback at each step along the way
 * @param {number} from Starting value
 * @param {number} to End value
 * @param {number} duration Duration of animation in milliseconds
 * @param {AnimateStepCallback} callback Called at 60 fps
 * @returns {() => void} Cancel the animation
 */


var animate = function animate(from, to, duration, callback) {
  var requestId = null;
  var start = performance.now();
  var distance = to - from;

  var step = function step() {
    var elapsed = performance.now() - start;

    if (elapsed < duration) {
      var travelled = easeInOut(elapsed / duration) * distance;
      callback(from + travelled);
      requestId = requestAnimationFrame(step);
    } else {
      callback(to);
    }
  };

  step();
  return function () {
    return cancelAnimationFrame(requestId);
  };
};
/**
 * @typedef {object} AccelerationInfo
 * @property {number} displacement
 * @property {number} velocity
 */

/**
 * @callback AccelerationStepCallback
 * @param {AccelerationInfo} info
 * @returns {void}
 */

/**
 * Accelerate from one velocity to another and call a callback with the
 * displacement while the acceleration is happening. Given the initial velocity,
 * final velocity and acceleration, we can figure out how long it will take to
 * get from one velocity to another and how far something has travelled while
 * accelerating.
 * @param {number} initialVelocity Initial velocity
 * @param {number} finalVelocity Velocity that we want to end at
 * @param {number} acceleration Rate at which to change velocity
 * @param {AccelerationStepCallback} callback Called while changing
 * velocity with the displacement depending on how much time has passed since
 * starting acceleration
 * @returns {() => void} Cancel the acceleration
 */


exports.animate = animate;

var accelerate = function accelerate(initialVelocity, finalVelocity, acceleration, callback) {
  var requestId = null;
  var start = performance.now(); // Calculate the total time that it will take to accelerate to the final
  // velocity. We use this value to decide how long the animation needs to run
  // We can get this value by solving for t in the formula used to find average
  // acceleration:
  // https://en.wikipedia.org/wiki/Acceleration#Average_acceleration

  var totalTime = (finalVelocity - initialVelocity) / acceleration;

  var step = function step() {
    var elapsed = Math.min(performance.now() - start, totalTime); // Calculate the displacement for how much time has elapsed so far.
    // https://en.wikipedia.org/wiki/Acceleration#Uniform_acceleration

    var displacement = initialVelocity * elapsed + 0.5 * acceleration * Math.pow(elapsed, 2);
    var velocity = initialVelocity + acceleration * elapsed;
    callback({
      displacement: displacement,
      velocity: velocity
    });

    if (elapsed < totalTime) {
      requestId = requestAnimationFrame(step);
    }
  };

  step();
  return function () {
    return cancelAnimationFrame(requestId);
  };
};

exports.accelerate = accelerate;